import React, { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useEffectiveRole, useCanSwitchView, VIEW_MODE_OPTIONS, viewModeLabel } from "@/lib/effectiveRole";
import { SignOut, House, Eye, X } from "@phosphor-icons/react";
import MobileBottomNav from "@/components/navigation/MobileBottomNav";
import MobileAppHeader from "@/components/navigation/MobileAppHeader";

const ROLE_LABEL = {
  investor: "Inversor",
  constructor: "Constructor",
  marketing: "Marketing",
  admin: "Admin · Equipo Dolce",
};

const ROLE_LABEL_EN = {
  investor: "Investor",
  constructor: "Constructor",
  marketing: "Marketing",
  admin: "Admin · Dolce Team",
};

const I18N = {
  es: { logout: "Cerrar sesión", footer: "Plataforma interna" },
  en: { logout: "Sign out", footer: "Internal platform" },
};

const ROLE_ACCENT = {
  investor: "#2F3E35",
  constructor: "#9C5236",
  marketing: "#C28F3C",
  admin: "#1C1C1E",
};

export default function Shell({ children, tabs, activeTab, onTabChange, roleOverride, lang = "es", tabsHiddenOnLg = false }) {
  const { user, logout, viewMode, setViewMode } = useAuth();
  const effectiveRole = useEffectiveRole();
  const canSwitchView = useCanSwitchView();
  const location = useLocation();
  const role = roleOverride || effectiveRole || user?.role || "investor";
  const accent = ROLE_ACCENT[role];
  const t = I18N[lang] || I18N.es;
  const roleLabel = lang === "en" ? ROLE_LABEL_EN[role] : ROLE_LABEL[role];
  // Router mode is activated when any tab has a `to` property
  const routerMode = Array.isArray(tabs) && tabs.some((t) => t && t.to);

  // Translate vertical wheel scroll into horizontal nav scroll. Keeps trackpad
  // and shift+wheel behaviour intact (they already emit deltaX). This makes the
  // overflowing admin tab strip reachable with a plain mouse wheel on desktop.
  const tabsScrollRef = useRef(null);
  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      const canScrollH = el.scrollWidth > el.clientWidth;
      if (!canScrollH) return;
      // If the user is scrolling vertically but the strip is overflowing,
      // redirect the gesture to horizontal. Don't fight horizontal gestures.
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && e.deltaY !== 0) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [tabs]);

  return (
    <div
      className="dv-app-viewport flex flex-col bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100"
      style={{ "--accent": accent }}
    >
      {/* Admin preview banner — sticky reminder + 1-click exit. Only ever
          shows when an admin has switched to a preview view.

          Cuando se enseña, ESTE es el elemento más alto de la pantalla (z-50,
          por encima de MobileAppHeader), así que es el que tiene que apartarse
          de la barra de estado de iOS. Sin el inset, el texto y la X de salir
          quedaban debajo del notch y no se podían tocar. En escritorio
          `env(safe-area-inset-top)` vale 0, así que esto sigue siendo py-2. */}
      {canSwitchView && viewMode && (
        <div
          className="sticky top-0 z-50 bg-violet-50 border-b border-violet-200 text-violet-900 text-sm"
          data-testid="view-preview-banner"
        >
          <div className="max-w-[1400px] mx-auto px-4 pb-2 pt-[calc(0.5rem_+_env(safe-area-inset-top,0px))] flex items-center justify-between gap-2">
            {/* El texto cede, el botón no. `flex-wrap` mandaba la X a una
                segunda línea en cuanto el texto no cabía —en cualquier móvil—
                y la dejaba lejos de donde se la busca. Ahora el bloque de
                texto se trunca (`min-w-0` + `truncate`) y el control conserva
                su tamaño: en una pantalla estrecha la prioridad es salir. */}
            <div className="flex items-center gap-2 min-w-0">
              <Eye size={14} className="shrink-0" />
              <span className="truncate">
                Vista preview: <strong>{viewModeLabel(viewMode, lang)}</strong>
                <span className="hidden sm:inline text-violet-700">
                  {" "}· los permisos reales no cambian
                </span>
              </span>
            </div>
            {/* 44x44 reales. Antes medía 26px de alto: justo debajo de la
                barra de estado, donde el dedo falla y acierta el sistema. */}
            <button
              type="button"
              onClick={() => setViewMode(null)}
              className="shrink-0 inline-flex items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] px-3 text-xs rounded border border-violet-300 bg-white hover:bg-violet-100"
              data-testid="view-preview-exit"
              aria-label="Volver a Vista Admin"
            >
              <X size={14} className="shrink-0" />
              <span>Volver a Vista Admin</span>
            </button>
          </div>
        </div>
      )}

      {/* Mobile-only app-style top bar — replaces desktop header on <lg */}
      <MobileAppHeader accent={accent} topInset={!(canSwitchView && viewMode)} />

      {/* Top Nav (desktop) */}
      <header className="hidden lg:block sticky top-0 z-40 glass border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md flex items-center justify-center text-white font-serif-dv text-lg" style={{ background: accent }}>
              D
            </div>
            <div>
              <div className="font-serif-dv text-xl leading-none">Dolce Villas</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 dark:text-stone-400">{roleLabel}</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {canSwitchView && (
              <label
                className="hidden md:inline-flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400"
                title="Cambiar la vista visual (los permisos no cambian)"
              >
                <Eye size={13} />
                <select
                  value={viewMode || "admin"}
                  onChange={(e) => setViewMode(e.target.value)}
                  className="border border-stone-200 dark:border-stone-700 rounded px-2 py-1 text-xs bg-white dark:bg-stone-900 dark:text-stone-100"
                  data-testid="desktop-view-switcher"
                >
                  {VIEW_MODE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium" data-testid="header-user-name">{user?.name}</div>
              {role !== "investor" && (
                <div className="text-xs text-stone-500 dark:text-stone-400">{user?.email}</div>
              )}
            </div>
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover border border-stone-200 dark:border-stone-700" />
            ) : (
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm"
                style={{ background: accent }}
              >
                {(user?.name || "U")[0]?.toUpperCase()}
              </div>
            )}
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="dv-btn dv-btn-ghost !p-2"
              title={t.logout}
            >
              <SignOut size={18} />
            </button>
          </div>
        </div>

        {/* Tabs — horizontally scrollable on every viewport when the strip
            exceeds the available width (the admin nav has 13 items and
            overflows even on 1440px laptops). We keep the right-edge mask
            as a hint of more content, hide the native scrollbar for a
            premium look, add `flex-nowrap`+`shrink-0` so items render at
            their natural width instead of being compressed, and forward
            vertical mouse-wheel gestures to horizontal scroll (handled in
            the effect above) so plain mice — not just trackpads — work. */}
        {tabs && (
          <div
            ref={tabsScrollRef}
            className={`max-w-[1400px] mx-auto px-6 overflow-x-auto overflow-y-hidden tabs-scroll-fade${tabsHiddenOnLg ? " lg:hidden" : ""}`}
          >
            <div className="flex flex-nowrap gap-1 pb-0 min-w-max">
              {tabs.map((t) => {
                // Router mode: NavLink driven by URL
                if (routerMode && t.to) {
                  const isActive = location.pathname === t.to
                    || (t.to !== "/dashboard" && location.pathname.startsWith(t.to + "/"));
                  return (
                    <NavLink
                      key={t.to}
                      to={t.to}
                      data-testid={`nav-${t.key || t.to.slice(1)}`}
                      className={`relative shrink-0 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${isActive ? "" : "text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"}`}
                      style={isActive ? { color: accent } : undefined}
                      aria-current={isActive ? "page" : undefined}
                    >
                      <span className="flex items-center gap-2">
                        {t.icon && <t.icon size={16} weight={isActive ? "fill" : "regular"} />}
                        {t.label}
                      </span>
                      {isActive && (
                        <div className="absolute bottom-0 left-3 right-3 h-0.5 rounded-t" style={{ background: accent }} />
                      )}
                    </NavLink>
                  );
                }
                // Legacy tab mode (investor/constructor/marketing keep this)
                const isActive = activeTab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => onTabChange(t.key)}
                    data-testid={`tab-${t.key}`}
                    className={`relative shrink-0 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${isActive ? "" : "text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"}`}
                    style={isActive ? { color: accent } : undefined}
                    aria-current={isActive ? "page" : undefined}
                  >
                    <span className="flex items-center gap-2">
                      {t.icon && <t.icon size={16} weight={isActive ? "fill" : "regular"} />}
                      {t.label}
                      {t.dot && (
                        <span
                          data-testid={`tab-dot-${t.key}`}
                          className="inline-block w-1.5 h-1.5 rounded-full"
                          style={{ background: "#E07A4F" }}
                        />
                      )}
                    </span>
                    {isActive && (
                      <div className="absolute bottom-0 left-3 right-3 h-0.5 rounded-t" style={{ background: accent }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <div className="max-w-[1400px] mx-auto px-6 py-8 dv-main-bottom-pad">{children}</div>
      </main>

      <footer className="border-t border-stone-200 dark:border-stone-800 py-6 px-6 text-center text-xs text-stone-400 dark:text-stone-500 hidden lg:block">
        Dolce Villas Lombok · {t.footer} · {new Date().getFullYear()}
      </footer>

      <MobileBottomNav activeTab={activeTab} />
    </div>
  );
}

export function StatCard({ label, value, sub, accent, testid }) {
  return (
    <div
      className="dv-card"
      data-testid={testid}
      style={accent ? { "--dv-accent": accent } : undefined}
    >
      <div className="text-xs tracking-widest uppercase text-stone-500 dark:text-stone-400 mb-2">{label}</div>
      <div className="font-serif-dv text-4xl leading-none dv-stat-value">{value}</div>
      {sub && <div className="text-sm text-stone-500 dark:text-stone-400 mt-2">{sub}</div>}
    </div>
  );
}

export function Section({ title, subtitle, action, children }) {
  return (
    <section className="mb-10">
      <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h2 className="font-serif-dv text-3xl leading-tight text-stone-900 dark:text-stone-50">{title}</h2>
          {subtitle && <p className="text-stone-500 dark:text-stone-400 mt-1">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
